import type { EIP1193Provider } from "viem";
import type { Connector } from "wagmi";
import { appChain, networkLabel } from "./constants";

function parseChainId(value: unknown): number {
  if (typeof value === "string") {
    return value.trim().toLowerCase().startsWith("0x")
      ? Number.parseInt(value, 16)
      : Number(value);
  }
  return Number(value);
}

/** Read the chain from the wallet provider itself, not only wagmi's cached state. */
export async function readWalletChainId(connector?: Connector): Promise<number | undefined> {
  const getProvider = connector?.getProvider;
  if (!getProvider) return undefined;
  const provider = await getProvider() as EIP1193Provider;
  const chainId = await provider.request({ method: "eth_chainId" });
  const parsed = parseChainId(chainId);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Switch and then verify the provider really moved to the configured BSC network. */
export async function ensureConfiguredBscNetwork(
  connector: Connector | undefined,
  currentChainId: number | undefined,
  switchChain: (args: { chainId: typeof appChain.id }) => Promise<unknown>
) {
  const walletChainId = await readWalletChainId(connector).catch(() => currentChainId);
  if (walletChainId !== appChain.id) {
    await switchChain({ chainId: appChain.id });
  }

  for (let attempt = 0; attempt < 10; attempt += 1) {
    const verifiedChainId = await readWalletChainId(connector).catch(() => undefined);
    if ((verifiedChainId ?? currentChainId) === appChain.id) return;
    await new Promise((resolve) => window.setTimeout(resolve, 200));
  }

  throw new Error(`钱包网络未切换到${networkLabel}（Chain ID ${appChain.id}），请在钱包中切换后重试`);
}
